import React, { useState, useEffect } from 'react';
import { inventoryAPI, productsAPI, customersAPI } from '../services/api';
import InventoryMovementsTable from '../components/InventoryMovementsTable';
import { isJuguteria } from '../hooks/useSucursal';
import './InventoryPage.css';
import './InventoryTienda.css';

/**
 * InventoryTienda — Vista de inventario para TIENDA GENERAL.
 * Mantiene el comportamiento original de InventoryPage (registrar movimientos +
 * historial por rango de fechas) con un header propio de tienda.
 */
const InventoryTienda = () => {
    const [movements, setMovements] = useState([]);
    const [products,  setProducts]  = useState([]);
    const [customers, setCustomers] = useState([]);
    const [filters,   setFilters]   = useState({ start_date: '', end_date: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');

    const [newMovement, setNewMovement] = useState({
        product_id:    '',
        movement_type: 'entrada',
        quantity:      '1',
        reason:        '',
        customer_id:   '',
        order_id:      ''
    });

    /* ── Carga inicial ───────────────────────────────────── */
    useEffect(() => {
        const load = async () => {
            try {
                const [pRes, cRes] = await Promise.all([
                    productsAPI.getAll(),
                    customersAPI.getAll()
                ]);
                setProducts(pRes.data.data.products || []);
                setCustomers(cRes.data.data.customers || []);
            } catch (err) {
                console.error('Error al cargar datos iniciales:', err);
            }
        };
        load();
    }, []);

    /* ── Historial ───────────────────────────────────────── */
    const fetchMovements = async () => {
        if (!filters.start_date || !filters.end_date) {
            alert('Por favor, selecciona un rango de fechas');
            return;
        }
        setIsLoading(true);
        try {
            const res = await inventoryAPI.getMovements(filters);
            setMovements(res.data.data.movements);
        } catch (err) {
            console.error('Error al cargar movimientos:', err);
        } finally {
            setIsLoading(false);
        }
    };

    /* ── Registrar movimiento ────────────────────────────── */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newMovement.product_id || !newMovement.quantity || !newMovement.reason) {
            alert('Por favor, completa todos los campos obligatorios del movimiento.');
            return;
        }
        try {
            await inventoryAPI.createMovement(newMovement);
            setSuccessMsg('✅ Movimiento registrado exitosamente');
            setNewMovement({ product_id: '', movement_type: 'entrada', quantity: '1', reason: '', customer_id: '', order_id: '' });
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err) {
            console.error('Error al registrar movimiento:', err);
            alert('Error al registrar el movimiento');
        }
    };

    const selectedProductInfo = products.find(p => p.id === parseInt(newMovement.product_id));

    return (
        <div className="content-pane inventory-page inv-tienda">

            {/* Header propio de tienda */}
            <div className="inv-tienda-header">
                <div className="inv-tienda-icon">{isJuguteria() ? '🧩' : '🛒'}</div>
                <div>
                    <h2>{isJuguteria() ? 'Control de Inventario — Jugutería' : 'Control de Inventario'}</h2>
                    <p>{isJuguteria() ? 'Registro de entradas de armables, sets de piezas y ajustes de stock' : 'Registro de entradas, salidas e historial de movimientos'}</p>
                </div>
            </div>

            {successMsg && (
                <div style={{
                    background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7',
                    borderRadius: 10, padding: '10px 16px', marginBottom: 16,
                    fontSize: 13, fontWeight: 600
                }}>
                    {successMsg}
                </div>
            )}

            <div className="inventory-grid">
                {/* FORMULARIO */}
                <form onSubmit={handleSubmit} className="inventory-card form-card">
                    <h3>➕ Registrar Movimiento</h3>

                    <div className="form-group">
                        <label>{isJuguteria() ? 'Armable / Producto *' : 'Producto *'}</label>
                        <select
                            name="product_id"
                            value={newMovement.product_id}
                            onChange={e => setNewMovement(p => ({ ...p, product_id: e.target.value }))}
                            required
                        >
                            <option value="">Seleccionar {isJuguteria() ? 'armable' : 'producto'}...</option>
                            {products.map(p => (
                                <option key={p.id} value={p.id}>
                                    {p.name} — {p.code} {isJuguteria() && p.numero_piezas ? `(${p.numero_piezas} piezas)` : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Tipo de Movimiento *</label>
                        <select
                            name="movement_type"
                            value={newMovement.movement_type}
                            onChange={e => setNewMovement(p => ({ ...p, movement_type: e.target.value }))}
                        >
                            <option value="entrada">📥 Entrada (Compra / Ajuste)</option>
                            <option value="salida">📤 Salida (Ajuste / Daño)</option>
                        </select>
                    </div>

                    {selectedProductInfo && (
                        <div className="product-info-panel">
                            {isJuguteria() && (
                                <>
                                    <div className="form-group">
                                        <label>Piezas</label>
                                        <input type="text" value={selectedProductInfo.numero_piezas ? `${selectedProductInfo.numero_piezas} piezas` : '-'} disabled />
                                    </div>
                                    <div className="form-group">
                                        <label>Dificultad</label>
                                        <input type="text" value={selectedProductInfo.nivel_dificultad ? selectedProductInfo.nivel_dificultad.toUpperCase() : '-'} disabled />
                                    </div>
                                </>
                            )}
                            <div className="form-group">
                                <label>Unidad</label>
                                <input type="text" value={selectedProductInfo.unit || selectedProductInfo.um || '-'} disabled />
                            </div>
                            <div className="form-group">
                                <label>Categoría</label>
                                <input type="text" value={selectedProductInfo.category || '-'} disabled />
                            </div>
                        </div>
                    )}

                    <div className="form-group">
                        <label>Cantidad *</label>
                        <input
                            type="number" name="quantity" min="1"
                            value={newMovement.quantity}
                            onChange={e => setNewMovement(p => ({ ...p, quantity: e.target.value }))}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Motivo / Observación *</label>
                        <input
                            type="text" name="reason"
                            value={newMovement.reason}
                            onChange={e => setNewMovement(p => ({ ...p, reason: e.target.value }))}
                            placeholder="Ej: Compra proveedor, Merma..."
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Cliente (Opcional)</label>
                        <select
                            name="customer_id"
                            value={newMovement.customer_id}
                            onChange={e => setNewMovement(p => ({ ...p, customer_id: e.target.value }))}
                        >
                            <option value="">— Ninguno —</option>
                            {customers.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Ref. Pedido / Factura (Opcional)</label>
                        <input
                            type="text" name="order_id"
                            value={newMovement.order_id}
                            onChange={e => setNewMovement(p => ({ ...p, order_id: e.target.value }))}
                            placeholder="N° Doc..."
                        />
                    </div>

                    <button type="submit" className="btn-submit">
                        📝 Registrar Movimiento
                    </button>
                </form>
            </div>

            {/* TARJETA 2: HISTORIAL */}
            <div className="inventory-card">
                <h4 className="card-header">Historial de Movimientos</h4>
                <div className="history-grid">
                    <div className="form-group">
                        <label>Desde:</label>
                        <input
                            type="date" name="start_date"
                            value={filters.start_date}
                            onChange={e => setFilters(p => ({ ...p, start_date: e.target.value }))}
                        />
                    </div>
                    <div className="form-group">
                        <label>Hasta:</label>
                        <input
                            type="date" name="end_date"
                            value={filters.end_date}
                            onChange={e => setFilters(p => ({ ...p, end_date: e.target.value }))}
                        />
                    </div>
                    <button onClick={fetchMovements} className="btn-search">🔍 Buscar</button>
                </div>
            </div>

            {/* TABLA */}
            {isLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>⏳ Cargando movimientos...</div>
            ) : movements.length > 0 ? (
                <InventoryMovementsTable movements={movements} />
            ) : (
                <div style={{ textAlign: 'center', color: '#888', marginTop: 20, padding: 20, background: '#fff', borderRadius: 8 }}>
                    Selecciona un rango de fechas para ver el historial.
                </div>
            )}
        </div>
    );
};

export default InventoryTienda;
